<script setup lang="ts">
import DRBtnIcon from "@/components/UI/DRBtnIcon.vue";
import DRConfirmPopup from "@/components/UI/feedbacks/DRConfirmPopup.vue";
import type { Layer } from "@/types/legend.types";
import type { InputInstance } from "element-plus";
import { computed, ref, watch } from "vue";
import LegendColorPicker from "./LegendColorPicker.vue";
import { getLayerLineweightOptions } from "./utils";
import { LAYER_LINETYPES } from "@/drawing/linetype/utils";
import { useTranslation } from "i18next-vue";

const props = defineProps<{
  modelValue: Layer;
}>();

const emits = defineEmits<{
  (e: "update:modelValue", value: Layer): void;
  (e: "remove"): void;
}>();

const layer = computed({
  get: () => props.modelValue,
  set: (value: Layer) => emits("update:modelValue", value),
});

const updateColor = (color: string | null) => {
  if (!color) return;
  layer.value = {
    ...layer.value,
    color,
  };
};

const lineweight = computed({
  get: () => layer.value.lineweight,
  set: (value?: number) => {
    layer.value = { ...layer.value, lineweight: value };
  },
});

const linetype = computed({
  get: () => layer.value.linetype,
  set: (value?: string) => {
    layer.value = { ...layer.value, linetype: value };
  },
});

const { t } = useTranslation();
// Fonction du t() actif plutôt qu'une const figée au chargement du module, pour rester
// à jour si la langue change (voir getLayerLineweightOptions).
const lineweightOptions = computed(() => getLayerLineweightOptions(t));
const linetypeOptions = LAYER_LINETYPES;

const nameInputRef = ref<InputInstance | null>(null);
const editingName = ref(false);
const name = ref("");

const onEditName = () => {
  editingName.value = true;
  name.value = layer.value.name;
};

const onValidateName = () => {
  const nameValue = name.value.trim();
  if (!nameValue || nameValue === layer.value.name) {
    editingName.value = false;
    name.value = layer.value.name;
    return;
  }
  editingName.value = false;
  layer.value = {
    ...layer.value,
    name: nameValue,
  };
};

watch(nameInputRef, (value) => {
  if (value && editingName.value) {
    value.focus();
  }
});
</script>

<template>
  <div
    class="d-flex gap-3 py-2 px-3 rounded mt-2 align-items-center hoverable-white"
  >
    <LegendColorPicker
      class="col-auto"
      :model-value="layer.color"
      @update:modelValue="updateColor"
    />

    <el-input
      v-if="editingName"
      class="col"
      v-model="name"
      ref="nameInputRef"
      @keyup.enter="onValidateName"
    ></el-input>

    <div v-else class="col">{{ layer.name }}</div>

    <el-select
      v-model="lineweight"
      :placeholder="$t('dashboard:lineweight')"
      clearable
      class="col-auto"
      style="width: 110px"
    >
      <el-option
        v-for="option in lineweightOptions"
        :key="option.value"
        :label="option.label"
        :value="option.value"
      />
    </el-select>

    <el-select
      v-model="linetype"
      :placeholder="$t('dashboard:linetype')"
      clearable
      class="col-auto"
      style="width: 130px"
    >
      <el-option
        v-for="option in linetypeOptions"
        :key="option"
        :label="option"
        :value="option"
      />
    </el-select>

    <div class="d-flex gap-1">
      <DRBtnIcon
        v-if="editingName"
        icon="bi-check-lg"
        variant="flat-success"
        :size="30"
        @click="onValidateName"
      />
      <DRBtnIcon
        v-else
        icon="bi-pencil"
        variant="flat"
        :size="30"
        @click="onEditName"
      />

      <DRConfirmPopup :title="$t('confirmDelete')" @confirm="$emit('remove')">
        <DRBtnIcon icon="bi-trash3" variant="flat-danger" :size="30" />
      </DRConfirmPopup>
    </div>
  </div>
</template>

<style lang="scss" scoped></style>
